import { ReplicantMigrator } from '@play-co/replicant';

// IMPORTANT: Do *NOT* add any other imports. Migrations must stay
// self-contained forever — inline any helper you need.
//
// MIGRATION SAFETY — same rules as src/replicant/migrations/migrator.ts:
// append-only (emergency no-op only for critically broken migrations —
// already-migrated states keep the broken shape and version), no rollback,
// mutate the provided state in place (return value ignored), guard every
// property access, two-step release for field removal/rename/retype, and a
// numbered test file (incl. empty-state {}) for every new migration.
// Details: .claude/rules/replicant.md ("State Migrations").
//
// Rollout: this is a SHARED-state migrator. It does not trigger the player
// AppInitFailed(errorCode = version_error) launch failure, but it is still
// mixed-version sensitive: code that sees a shared-state item at a higher
// version than it knows falls back to containment behavior (e.g. skipping
// message reduction). Ship migrations standalone, roll out as fast as is safe,
// and verify old-new version interactions.

const migrator = new ReplicantMigrator();

/**
 * Set team event type and score
 * Before that team score was calculated on the fly. based on current members contributions, but that could cause issues when members
 * leave the team, decreasing the team score: an event that has been completed could turn to be incomplete after that.
 * The event type will be necessary for different types cof events - we had only teams competiton so far, so default to 'competition'
 */
migrator.addMigration(1, (state) => {
  // Calculate event overall team score from current members contributions.
  const getTeamEventScore = (eventId: string) => {
    return Object.values(state.members).reduce((score, member) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const memberScore = (member as any).contributions[eventId]?.score ?? 0;
      return score + memberScore;
    }, 0);
  };

  // Loop through all events to calculate score.
  for (const eventId in state.events) {
    const event = state.events[eventId];

    // Set modifiedAt to same as createdAt, if undefined.
    if (event.modifiedAt === undefined) {
      event.modifiedAt = event.createdAt;
    }

    // Calculate total score for event, if undefined.
    if (event.score === undefined) {
      event.score = getTeamEventScore(eventId);
    }

    // Set the event type to competition, if undefined.
    if (event.type === undefined) {
      event.type = 'competition';
    }
  }
});

/**
 * Remove old team events.
 * Everything before 19th of match 2024 is considered old.
 */
migrator.addMigration(2, (state) => {
  const threshold = new Date('2024-03-19T00:00:00+09:00').getTime();
  const oldEvents = Object.keys(state.events ?? {}).filter((eventId) => {
    return state.events?.[eventId].createdAt < threshold;
  });

  oldEvents.forEach((eventId) => {
    if (state.events?.[eventId]) {
      delete state.events[eventId];
    }
  });

  Object.values(state.members ?? {}).forEach((member: any) => {
    for (const eventId of oldEvents) {
      if (member.contributions?.[eventId]) {
        delete member.contributions[eventId];
      }
    }
  });
});

/**
 * Remove old team help requests.
 * Request that created more that 30 days ago considered old.
 */
migrator.addMigration(3, (state) => {
  const threshold = new Date('2024-05-01T00:00:00+09:00').getTime();

  Object.values(state.members ?? {}).forEach((member: any) => {
    if (!member?.helps) return;

    member.helps = member.helps.filter(
      (help: any) => help.createdAt >= threshold,
    );
  });
});

/**
 * Copy createdAt to createdTimestamp
 * Required before upgrading to replicant 30
 */
migrator.addMigration(4, (state) => {
  if (state.createdAt && !state.createdTimestamp) {
    state.createdTimestamp = state.createdAt;
  }
});

/**
 * Remove createdAt
 * Required before upgrading to replicant 30
 */
migrator.addMigration(5, (state) => {
  if ('createdAt' in state) {
    delete state.createdAt;
  }
});

// Add new migrations right above this line. Do *NOT* modify existing migrations.

export default migrator;
